import { useEffect } from 'react';
import { Outlet } from 'react-router';

import { Icon } from '@ds/core';
import { LinksLegais } from '@/comuns/documentoLegal';
import { aplicarTema, observarSistema, temaSalvo } from '@/tema';
import logoSobreEscuro from '@assets/formulamed-logo-branco.png';

const DESTAQUES = [
    { icone: 'calendar-days', texto: 'Agenda integrada' },
    { icone: 'clipboard-list', texto: 'Prontuário eletrônico' },
    { icone: 'chart-column', texto: 'Gestão inteligente' },
];

/* A moldura fora de sessão tem desenho próprio (referência do usuário, 15/09/2026): Deep
   Clinic com brilho verde que se move devagar. O movimento é só `transform` — nunca
   `opacity` a partir de zero —, então a tela parada, seja qual for o instante, já é a tela
   certa; e quem pede menos movimento no sistema vê o fundo parado. */
const ESTILO = `
.fm-auth{position:relative;min-height:100vh;display:flex;flex-direction:column;overflow:hidden;background:var(--fm-deep-clinic);color:var(--text-primary);font-family:var(--font-ui)}
.fm-auth-fundo{position:absolute;inset:0;pointer-events:none;overflow:hidden}
.fm-auth-bolha{position:absolute;border-radius:50%;filter:blur(64px);will-change:transform}
.fm-auth-bolha--1{width:64vmax;height:64vmax;left:-24vmax;bottom:-32vmax;background:radial-gradient(circle,rgba(50,242,141,.30),rgba(50,242,141,0) 64%);animation:fm-auth-deriva-1 38s ease-in-out infinite alternate}
.fm-auth-bolha--2{width:50vmax;height:50vmax;right:-18vmax;top:-20vmax;background:radial-gradient(circle,rgba(11,142,84,.36),rgba(11,142,84,0) 64%);animation:fm-auth-deriva-2 46s ease-in-out infinite alternate}
.fm-auth-bolha--3{width:34vmax;height:34vmax;right:6vmax;bottom:-14vmax;background:radial-gradient(circle,rgba(50,242,141,.16),rgba(50,242,141,0) 64%);animation:fm-auth-deriva-3 54s ease-in-out infinite alternate}
.fm-auth-arco{position:absolute;border:1px solid rgba(50,242,141,.24);border-radius:50%}
.fm-auth-arco--lado{width:36vmax;height:36vmax;right:-24vmax;top:20vh;animation:fm-auth-deriva-2 60s ease-in-out infinite alternate}
.fm-auth-arco--base{width:150vmax;height:62vmax;left:-24vmax;bottom:-52vmax;border-color:rgba(50,242,141,.16)}
@keyframes fm-auth-deriva-1{from{transform:translate3d(0,0,0) scale(1)}to{transform:translate3d(6vmax,-4vmax,0) scale(1.08)}}
@keyframes fm-auth-deriva-2{from{transform:translate3d(0,0,0) scale(1.04)}to{transform:translate3d(-5vmax,5vmax,0) scale(.96)}}
@keyframes fm-auth-deriva-3{from{transform:translate3d(0,0,0)}to{transform:translate3d(-7vmax,-3vmax,0)}}
@media (prefers-reduced-motion: reduce){.fm-auth-bolha,.fm-auth-arco{animation:none}}
.fm-auth-grade{position:relative;z-index:1;flex:1;width:100%;max-width:1240px;margin:0 auto;padding:clamp(28px,6vh,72px) clamp(16px,5vw,64px);display:grid;grid-template-columns:minmax(0,1.15fr) minmax(0,460px);gap:clamp(32px,6vw,96px);align-items:center}
.fm-auth-heroi{display:flex;flex-direction:column;gap:26px;min-width:0}
.fm-auth-logo{height:clamp(30px,3.4vw,46px);width:auto;max-width:100%;align-self:flex-start}
.fm-auth-titulo{margin:0;font-family:var(--font-display);font-weight:600;font-size:clamp(34px,4.6vw,60px);line-height:1.06;letter-spacing:-.025em;color:var(--fm-clinical-white);text-wrap:balance}
.fm-auth-titulo em{font-style:normal;color:var(--fm-formula-green)}
.fm-auth-sub{margin:-10px 0 0;font-size:clamp(15px,1.4vw,19px);line-height:1.5;color:#C6D3CE}
.fm-auth-destaques{list-style:none;margin:6px 0 0;padding:0;display:flex;flex-direction:column;gap:12px}
.fm-auth-destaques li{display:flex;align-items:center;gap:14px;font-size:15px;color:#DCE5E1}
.fm-auth-icone{width:44px;height:44px;flex:0 0 auto;border-radius:12px;display:flex;align-items:center;justify-content:center;color:var(--fm-formula-green);background:rgba(50,242,141,.08);border:1px solid rgba(50,242,141,.18)}
.fm-auth-cartao{min-width:0;background:rgba(10,42,37,.74);border:1px solid rgba(243,246,242,.12);border-radius:var(--radius-2xl);padding:clamp(22px,3.2vw,40px);box-shadow:0 24px 64px rgba(0,0,0,.32);backdrop-filter:blur(10px);-webkit-backdrop-filter:blur(10px)}
.fm-auth-monograma{width:64px;height:64px;margin:0 auto 18px;border-radius:16px;display:flex;align-items:center;justify-content:center;background:var(--fm-deep-clinic);border:1px solid rgba(50,242,141,.45);box-shadow:0 0 0 1px rgba(50,242,141,.10),0 0 28px rgba(50,242,141,.22);font-family:var(--font-display);font-weight:700;font-size:24px;letter-spacing:-.03em}
.fm-auth-canto{position:absolute;z-index:1;margin:0;font-family:var(--font-mono);font-size:11px;line-height:1.7;letter-spacing:.14em;text-transform:uppercase;color:var(--fm-muted)}
.fm-auth-canto::after{content:"";display:block;width:32px;height:1px;margin-top:10px;background:var(--fm-formula-green)}
.fm-auth-canto--topo{top:clamp(18px,4vh,44px);right:clamp(16px,3vw,44px)}
.fm-auth-canto--base{bottom:clamp(18px,4vh,44px);right:clamp(16px,3vw,44px)}
.fm-auth-rodape{position:relative;z-index:1;width:100%;max-width:1240px;margin:0 auto;padding:0 clamp(16px,5vw,64px) 28px;display:flex;flex-wrap:wrap;align-items:center;gap:10px 28px}
.fm-auth-assinatura{display:flex;align-items:center;gap:12px;font-family:var(--font-mono);font-size:11px;letter-spacing:.14em;text-transform:uppercase;color:var(--fm-muted)}
.fm-auth-assinatura::before{content:"";width:32px;height:1px;background:var(--fm-muted)}
@media (max-width:1080px){.fm-auth-canto{display:none}}
@media (max-width:900px){
  .fm-auth-grade{grid-template-columns:minmax(0,1fr);max-width:560px;gap:28px;align-items:start}
  .fm-auth-heroi{gap:14px}
  .fm-auth-destaques{display:none}
  .fm-auth-titulo{font-size:clamp(26px,7vw,38px)}
  .fm-auth-sub{margin:0}
  .fm-auth-rodape{justify-content:center}
}
`;

/**
 * Moldura fora de sessão (entrar, criar conta, recuperar senha): a marca à esquerda e o
 * cartão com o formulário à direita. Escura nos dois temas — `data-theme="dark"` no
 * contêiner põe os tokens do escuro só aqui dentro.
 */
export function AuthShell() {
    /* O tema é da pessoa, não da sessão. Esta moldura é sempre escura, mas o tema salvo
       segue valendo para as telas de dentro. */
    useEffect(() => {
        const tema = temaSalvo();
        aplicarTema(tema);
        return observarSistema(tema, () => aplicarTema(tema));
    }, []);

    return (
        <div className="fm-auth" data-theme="dark">
            <style>{ESTILO}</style>
            <div className="fm-auth-fundo" aria-hidden="true">
                <span className="fm-auth-bolha fm-auth-bolha--1" />
                <span className="fm-auth-bolha fm-auth-bolha--2" />
                <span className="fm-auth-bolha fm-auth-bolha--3" />
                <span className="fm-auth-arco fm-auth-arco--lado" />
                <span className="fm-auth-arco fm-auth-arco--base" />
            </div>
            <p className="fm-auth-canto fm-auth-canto--topo" aria-hidden="true">Clínicas<br />mais humanas,<br />mais eficientes.</p>
            <p className="fm-auth-canto fm-auth-canto--base" aria-hidden="true">Tecnologia<br />que cuida<br />de pessoas.</p>

            <main className="fm-auth-grade">
                <section className="fm-auth-heroi" aria-label="Fórmula MED">
                    {/* O logotipo é arte proprietária: nunca redigitado em fonte do sistema. */}
                    <img src={logoSobreEscuro} alt="Fórmula MED" className="fm-auth-logo" />
                    <p className="fm-auth-titulo">Sua clínica inteira em <em>uma só Fórmula</em></p>
                    <p className="fm-auth-sub">Gestão, cuidado e inteligência conectados.</p>
                    <ul className="fm-auth-destaques">
                        {DESTAQUES.map((d) => (
                            <li key={d.texto}>
                                <span className="fm-auth-icone" aria-hidden="true"><Icon name={d.icone} size={20} /></span>
                                {d.texto}
                            </li>
                        ))}
                    </ul>
                </section>

                <div className="fm-auth-cartao">
                    {/* Monograma FM do design system: F em branco clínico, M em verde. */}
                    <div className="fm-auth-monograma" aria-hidden="true">
                        <span style={{ color: 'var(--fm-clinical-white)' }}>F</span>
                        <span style={{ color: 'var(--fm-formula-green)' }}>M</span>
                    </div>
                    <Outlet />
                </div>
            </main>

            <footer className="fm-auth-rodape">
                <span className="fm-auth-assinatura">
                    Fórmula MED
                    <span aria-hidden="true">|</span>
                    <span style={{ textTransform: 'none', letterSpacing: 0 }}>Saúde em evolução.</span>
                </span>
                {/* A verificação do app no Google pede a Política visível a partir da página inicial. */}
                <LinksLegais />
            </footer>
        </div>
    );
}
